import React from 'react';
import { useTranslation } from "react-i18next"
import { Typography, Grid, makeStyles, Box, useTheme, useMediaQuery, Link } from '@material-ui/core';
import Card from './Card';
import { isEmpty, get } from "lodash";
import DivergingBarChart from "../D3Charts/DivergingBartChart"
import { changeTab, setViewBy } from "../../reduxLib/services"
import { useSelector, useDispatch } from 'react-redux';
import { performanceTableStyle } from 'theme'
import HelpPopover from './HelpPopover';
import { netHoursInfoLink } from 'configs/appConstants';

const useStyles = makeStyles(performanceTableStyle);

export const TooltipContent = ({ item }) => {
  const classes = useStyles();
  const avgWorkFraction = item?.avgWorkFraction == 0 ? 1 : item?.avgWorkFraction;
  return <Link target="_blank" href={netHoursInfoLink} className={classes.fullWidth} data-testid="netHoursTooltip">
    <Typography variant="body1" className={classes.helperText}>
      {`${item?.netHoursAhead} - ${item?.netHoursBehind}`}  <hr /> {avgWorkFraction}
    </Typography>
  </Link>
}

const PerformanceMapTable = ({ data, perfTableRedirection, type, singleLocation, pageName }) => {

  const classes = useStyles({ singleLocation });
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const locationCoreData = useSelector(({ sites }) => sites.locations || [])
  const showTabsBy = useSelector(({ options }) => get(options, "showTabsBy", null))
  const isMobile = useMediaQuery(useTheme().breakpoints.down("sm"));

  const getTitle = () => {
    const keyMap = {
      DOT: 'dot_name',
      NONDOT: 'non_dot_name',
    };
    const key = singleLocation && data?.length === 1
      ? keyMap[data?.[0]?.dotType] ?? 'non_dot_name' // fallback if unknown dotType
      : 'locations';

    return t(key);
  };


  const handleRowClick = async (item) => {
    const selectedSiteData =
      locationCoreData.find((site) => site.siteNum === item.siteNum);
    if (!selectedSiteData) return;

    if (showTabsBy === "customer" || showTabsBy === null) {
      dispatch(setViewBy("locations"));
    }
    await dispatch(changeTab(selectedSiteData));
    // Batch call for two state changes
    perfTableRedirection(selectedSiteData.siteNum, type);
  };


  return (
    <Grid container className={classes.main} data-testid="performancetable">
      <Card innercard className={classes.innerCard}>
        <Box pl={9} pr={4} pb={2}>
          <Grid container item>
            <Grid container item xs={12} sm={12} className={classes.tableContainer}>
              {/* Header */}
              <Grid
                container
                item
                xs={12}
                className={classes.tableHeader}
                justifyContent="space-around"
              // alignItems="center"
              >
                <Grid item xs={6} sm={6} md={3}>
                  <Typography variant="h3">{getTitle()}</Typography>
                </Grid>

                {!isMobile && <Grid item md={1}>
                  <Typography align="center" variant="h3">{t('plant')}</Typography>
                </Grid>}

                {!isMobile && <Grid item md={1}>
                  <Typography variant="h3">
                    {t('status')}
                  </Typography>
                </Grid>}

                {!isMobile && <Grid item md={3}>
                  <Typography align="center" variant="h3">
                    {t('dvs_volume')}
                  </Typography>
                </Grid>}

                <Grid item xs={6} sm={6} md={4}>
                  <Typography align="center" variant="h3">
                    {t('net_loading_hours')}
                    <HelpPopover testId="help-button">
                      <Link target="_blank" href={netHoursInfoLink} >
                        <Typography variant="body1" className={classes.helperText}>{t('net_hours_ahead_behind')}  <hr /> {t('average_work_hour')}</Typography>
                      </Link>
                    </HelpPopover>
                  </Typography>
                </Grid>
              </Grid>

              {/* Body */}
              {isEmpty(data) ? (
                <Grid
                  container
                  item
                  xs={12}
                  justifyContent="center"
                  alignItems="baseline"
                  className={classes.nodata}
                  data-testid="noSiteData"
                >
                  <Typography color="primary">{t('no_data_found')}</Typography>
                </Grid>
              ) : (
                <>
                  {/* Left: Table */}
                  <Grid item xs={6} sm={6} md={8} lg={8}>
                    {data.map((item) => (
                      <Grid
                        container
                        item
                        xs={12}
                        key={item?.siteNum ?? item?.shortName}
                        className={classes.tableBody}
                        alignItems="center"
                        data-testid="sitesData"
                      >
                        {/* Name */}
                        <Grid item xs={6} sm={6} md={5}>
                          <Typography variant="h6" color="primary">
                            {pageName !== 'distribution' ? (
                              <span
                                data-testid="distributionlink_name"
                                id={item?.siteNum}
                                className={classes.link}
                                onClick={() => handleRowClick(item)}
                              >
                                {item?.shortName}
                              </span>
                            ) : (
                              <span
                                data-testid="distributionlink_name"
                                id={item?.siteNum}
                                className={classes.nonPointerLink}
                              >
                                {item?.shortName}
                              </span>
                            )}
                          </Typography>
                        </Grid>

                        {/* Plant / SiteNum */}
                        {!isMobile && <Grid item xs={6} sm={6} md={1}>
                          <Typography variant="h6" color="primary">
                            {pageName !== 'distribution' ? (
                              <span
                                data-testid="distributionlink_siteNum"
                                id={item?.siteNum}
                                className={classes.link}
                                onClick={() => handleRowClick(item)}
                              >
                                {item?.siteNum ?? ''}
                              </span>
                            ) : (
                              <span
                                data-testid="distributionlink_siteNum"
                                id={item?.siteNum}
                                className={classes.nonPointerLink}
                              >
                                {item?.siteNum ?? ''}
                              </span>
                            )}
                          </Typography>
                        </Grid>}

                        {/* Status */}
                        {!isMobile && <Grid item xs={6} sm={6} md={1}>
                          <Typography align="center" variant="h6" color="primary">
                            {t(item?.status)}
                          </Typography>
                        </Grid>}

                        {/* DVS Volume / Type */}
                        {!isMobile && <Grid item xs={6} sm={6} md={5}>
                          <Typography align="center" variant="h6" color="primary">
                            {item?.dvsPercentComplete === 0 ? 'N/A' : item?.dvsPercentComplete}
                          </Typography>
                        </Grid>}
                      </Grid>
                    ))}
                  </Grid>

                  {/* Right: Chart */}
                  <Grid item xs={6} sm={6} md={4} lg={4} className={classes.chart}>
                    <DivergingBarChart data={data} TooltipContent={TooltipContent} />
                  </Grid>
                </>
              )}
            </Grid>
          </Grid>
        </Box>
      </Card>
    </Grid>
  );
};

export default PerformanceMapTable;
